import React, { useState, useCallback } from 'react';
import { Sun } from 'lucide-react';

export interface BrightnessStyle01Props {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  loading?: boolean;
  onChange: (value: number) => void;
  onChangeEnd?: (value: number) => void;
  label?: string;
  showIcon?: boolean;
}

/**
 * Brightness Style 01 - Horizontal Slider
 * Command: BrightnessAbsolute
 * Classic horizontal slider with percentage display
 */
export const Style01_Slider: React.FC<BrightnessStyle01Props> = ({
  value,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  loading = false,
  onChange,
  onChangeEnd,
  label = '亮度',
  showIcon = true
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);

  const displayValue = isDragging ? localValue : value;
  const percentage = ((displayValue - min) / (max - min)) * 100;

  // Sync local value with prop when not dragging
  React.useEffect(() => {
    if (!isDragging) {
      setLocalValue(value);
    }
  }, [value, isDragging]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    setLocalValue(newValue);
    // Don't call onChange here - wait for release
  }, []);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    // Only call API on release
    if (onChangeEnd) {
      onChangeEnd(localValue);
    } else {
      onChange(localValue);
    }
  }, [localValue, onChange, onChangeEnd]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {showIcon && <Sun className="w-4 h-4 text-yellow-400" />}
          <span className="text-sm font-medium text-slate-300">{label}</span>
        </div>
        <span className="text-sm font-bold text-yellow-400">
          {loading ? '...' : `${displayValue}%`}
        </span>
      </div>
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={displayValue}
          onChange={handleChange}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleMouseUp}
          disabled={disabled || loading}
          className="brightness-slider w-full h-2 rounded-full appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: `linear-gradient(to right, #facc15 0%, #facc15 ${percentage}%, #334155 ${percentage}%, #334155 100%)`
          }}
        />
      </div>
      <div className="flex justify-between text-xs text-slate-500">
        <span>{min}%</span>
        <span>{max}%</span>
      </div>
    </div>
  );
};

export default Style01_Slider;
